import _ from "lodash";
import moment from "moment-timezone";
import pluralize from "pluralize";
import { humanizeTimestamp } from "common/utils/date-helper";

export const OUTSTANDING = "outstanding";
export const CANDIDATE_REQUESTS_RESCHEDULE = "candidate_requests_reschedule";
export const CANDIDATE_REQUESTS_MORE_AVAILABILITY =
  "candidate_requests_more_availability";
export const INTERVIEW_REQUEST_OUTSTANDING_STATES = [
  OUTSTANDING,
  CANDIDATE_REQUESTS_RESCHEDULE,
  CANDIDATE_REQUESTS_MORE_AVAILABILITY,
];
export const INTERVIEW_REQUEST_CONFIRMED_STATES = ["confirmed"];
export const INTERVIEW_REQUEST_POST_CONFIRMED_STATES = [
  "no_show_candidate",
  "no_show_employer",
];

export const interviewRequestTypes = () => {
  return [
    ["skype", "Skype"],
    ["hangout", "Google Meet"],
    ["zoom", "Zoom"],
    ["ms_teams", "Microsoft Teams"],
    ["phone", "Phone"],
    ["onsite", "On-site (in-person)"],
    ["bluejeans", "BlueJeans"],
  ];
};

export const getInterviewTypeDisplay = (type) => {
  const d = _.find(interviewRequestTypes(), (val) => val[0] === type);
  if (d) {
    return d[1];
  }
  return _.capitalize(type);
};

export const isUpcoming = (interviewRequest) => {
  if (!interviewRequest.end_time) {
    return null;
  }

  return moment.unix(interviewRequest.end_time) > moment();
};

export const calendarTimeFormatter = {
  lastWeek: "MMM D, YYYY",
  sameDay: "h:mma z [today] (MMM D, YYYY)",
  nextDay: "h:mma z dddd (MMM D, YYYY)",
  nextWeek: "h:mma z dddd (MMM D, YYYY)",
  sameElse: "h:mma z dddd (MMM D, YYYY)",
};

export const confirmationTimeChanged = (changeList) => {
  return _.includes(changeList, "start_time");
};

export const formatDuration = (durationInSeconds, plural = false) => {
  const durationInHours = durationInSeconds / 60 / 60;

  if (durationInHours >= 1) {
    return plural
      ? `${pluralize("hours", durationInHours, true)}`
      : `${durationInHours} hour`;
  }
  return `${durationInHours * 60} ${!plural ? "minutes" : "minute"}`;
};

export const changedDetailStatus = (interview, transition, changedProperty) => {
  let text;
  const changedField = Object.keys(changedProperty)[0];
  const newValue = changedProperty[changedField];

  switch (changedField) {
    case "interview_type":
      text = `HireArt changed the interview type to ${getInterviewTypeDisplay(
        newValue
      )}`;
      return {
        employer: text,
        candidate: `${text}.`,
      };
    case "duration":
      text = `HireArt changed the interview duration to ${formatDuration(
        interview.duration,
        true
      )}`;
      return {
        employer: text,
        candidate: `${text}.`,
      };
    case "preferred_phone":
    case "skype_username_candidate":
      return {
        employer: "HireArt updated the candidate's contact information",
        candidate: `HireArt updated your contact information to ${newValue}`,
      };
    case "start_time":
      text = "HireArt rescheduled interview for";
      return {
        employer: `${text} ${moment
          .unix(interview.start_time)
          .calendar(Date.now(), calendarTimeFormatter)}`,
        candidate: `${text} ${moment
          .unix(interview.start_time)
          .tz(moment.tz.guess())
          .format("MMM D [at] h:mma z")}.`,
      };
    case "zoom_url":
    case "ms_teams_url":
    case "hangout_url":
    case "bluejeans_meetingid":
      return {
        employer: `HireArt updated your contact information to (${newValue})`,
        candidate: `${
          changedField === "bluejeans_meetingid"
            ? "BlueJeans Url/meeting ID"
            : `${getInterviewTypeDisplay(transition.interview_type)} url`
        } updated to (${newValue})`,
      };
    case "onsite":
      return {
        employer: `HireArt updated the interview location address to (${newValue})`,
        candidate: `HireArt updated the interview location address to (${newValue})`,
      };
    default:
      return {};
  }
};

export const confirmedInterviewChangeStatus = (
  interview,
  transition,
  changedDetails
) => {
  const changedKeys = Object.keys(changedDetails);
  if (changedKeys.length > 1) {
    let employerStatus = "HireArt updated interview details";
    let candidateStatus = employerStatus;

    if (confirmationTimeChanged(changedKeys)) {
      employerStatus += ` and rescheduled for ${moment
        .unix(interview.start_time)
        .calendar(Date.now(), calendarTimeFormatter)}`;
      candidateStatus += ` and rescheduled for ${moment
        .unix(interview.start_time)
        .tz(moment.tz.guess())
        .format("MMM D [at] h:mma z")}.`;
    }

    return {
      employer: employerStatus,
      candidate: candidateStatus,
    };
  }
  return changedDetailStatus(interview, transition, changedDetails);
};

// action copy means call to action
export const getInterviewStatus = (interviewRequest) => {
  const interviewTimeFormatted = humanizeTimestamp(interviewRequest.start_time);

  switch (interviewRequest.step) {
    case "outstanding":
      return {
        color: "warning",
        text: "Unconfirmed",
        details: "Confirm a time to finish scheduling an interview.",
        action: "Choose time",
      };
    case "candidate_requests_more_availability":
      return {
        color: "warning",
        text: "Pending interviewer availability",
        details:
          "You indicated that none of the suggested interview times work for you. We'll send you a new interview request when additional interview times can be determined.",
      };
    case "confirmed":
      if (isUpcoming(interviewRequest)) {
        return {
          color: "blue",
          text: "Confirmed",
          details: `Interview scheduled for ${interviewTimeFormatted}`,
        };
      }
      return {
        color: "success",
        text: "Confirmed",
        details: `Interview took place on ${interviewTimeFormatted}`,
      };

    case "canceled_by_employer":
      return {
        text: "Inactive",
        details: "Interviewer canceled the interview.",
      };
    case "expired":
      return {
        text: "Inactive",
        details: "You didn't respond, so we closed this request.",
      };
    case "canceled_by_candidate":
      return {
        text: "Inactive",
        details: "You declined the interview request.",
      };
    case "candidate_requests_reschedule":
      return {
        color: "warning",
        text: "Pending interviewer's availability",
        details: "You asked to reschedule this interview.",
      };
    case "no_show_candidate":
      return {
        text: "Interview missed",
        details: "You missed the interview",
      };
    case "no_show_employer":
      return {
        text: "Interview missed",
        details: "Interviewer missed the interview",
      };
    default:
      return null;
  }
};

export const getStartTime = ({ date, minutesAfterMidnight, timezone }) => {
  const timezoneOffset = moment.tz(timezone).format("Z");

  // Convert the date object to a moment object
  const momentDate = moment(date);

  return moment(momentDate.format(`YYYY-MM-DDT00:00:00${timezoneOffset}`))
    .add(minutesAfterMidnight, "minutes")
    .unix();
};
